import React, { useState } from 'react';
import PropTypes from 'prop-types';
import OccupancyIndicator from './OccupancyIndicator';

const getAccentColor = (popularity, status) => {
  if (status === 'CLOSED_TEMPORARILY') return 'bg-gray-300';
  if (popularity === null) return 'bg-gray-200';
  if (popularity < 30) return 'bg-green-400';
  if (popularity < 70) return 'bg-honey-500';
  return 'bg-red-400';
};

const getOccupancyBadge = (popularity, status) => {
  if (status === 'CLOSED_TEMPORARILY') {
    return {
      text: 'Closed',
      bg: 'bg-gray-100 dark:bg-gray-700/50',
      text_color: 'text-gray-500 dark:text-gray-400',
      icon: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z',
    };
  }
  if (popularity === null) {
    return {
      text: 'No data',
      bg: 'bg-gray-50 dark:bg-gray-700/30',
      text_color: 'text-gray-400 dark:text-gray-500',
      icon: 'M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01',
    };
  }
  if (popularity < 30) {
    return {
      text: 'Quiet',
      bg: 'bg-green-50 dark:bg-green-900/30',
      text_color: 'text-green-700 dark:text-green-400',
      icon: 'M5 13l4 4L19 7',
    };
  }
  if (popularity < 70) {
    return {
      text: 'Moderate',
      bg: 'bg-honey-50 dark:bg-honey-900/30',
      text_color: 'text-honey-700 dark:text-honey-400',
      icon: 'M13 10V3L4 14h7v7l9-11h-7z',
    };
  }
  return {
    text: 'Busy',
    bg: 'bg-red-50 dark:bg-red-900/30',
    text_color: 'text-red-600 dark:text-red-400',
    icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
  };
};

const PlaceCard = ({ place, buttonText, onButtonClick, disabled }) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsLoading(true);
    const button = event.currentTarget;
    const rect = button.getBoundingClientRect();

    [-5, 0, 5].forEach((offset, index) => {
      setTimeout(() => {
        const drop = document.createElement('div');
        drop.className = 'honey-drop';
        drop.style.left = `${rect.width / 2 + offset}px`;
        drop.style.top = `${rect.height / 2}px`;
        button.appendChild(drop);
        setTimeout(() => drop.remove(), 1000);
      }, index * 150);
    });

    setTimeout(() => {
      onButtonClick(place);
      setIsLoading(false);
    }, 800);
  };

  const getMapsUrl = (placeName, placeId) => {
    if (placeId) {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(placeName)}&query_place_id=${placeId}`;
    }
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(placeName)}`;
  };

  const handleCardClick = (event) => {
    if (event.target.closest('button') || event.target.closest('a')) return;
    window.open(getMapsUrl(place.name, place.place_id), '_blank', 'noopener,noreferrer');
  };

  const badge = getOccupancyBadge(place.current_popularity, place.business_status);
  const isSaved = disabled;
  const isRemove = buttonText === 'Remove';

  return (
    <div
      onClick={handleCardClick}
      className="group relative glass-card transition-all duration-300
        hover:shadow-glass-lg hover:-translate-y-0.5 hover:scale-[1.01] active:scale-[0.99] cursor-pointer w-full"
      role="button"
      tabIndex={0}
      aria-label={`View ${place.name} on Google Maps`}
      onKeyPress={(e) => e.key === 'Enter' && handleCardClick(e)}
    >
      {/* Left accent bar */}
      <div
        className={`absolute left-0 top-0 bottom-0 w-1 ${getAccentColor(place.current_popularity, place.business_status)} rounded-l-2xl`}
      />

      <div className="p-4 pl-5 relative">
        {/* Action button — top right */}
        <button
          onClick={handleClick}
          disabled={disabled || isLoading}
          aria-label={disabled ? 'Already in favorites' : `Add ${place.name} to favorites`}
          className={`absolute top-3 right-3 z-10 flex items-center gap-1.5 px-3 py-1.5 min-h-[36px] sm:min-h-[44px] rounded-full
            text-xs font-medium transition-all duration-300 active:scale-95
            ${
              isSaved
                ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-default'
                : isRemove
                  ? 'bg-red-50 dark:bg-red-900/30 text-red-500 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/50 hover:shadow-md'
                  : 'bg-honey-500 text-white hover:bg-honey-600 hover:shadow-md shadow-honey-500/20'
            }
            ${isLoading ? 'animate-pulse' : ''}`}
        >
          {isLoading ? (
            <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
          ) : (
            <>
              {isSaved ? (
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                </svg>
              ) : isRemove ? (
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              ) : (
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                  />
                </svg>
              )}
              <span className="hidden sm:inline">{buttonText}</span>
            </>
          )}
        </button>

        {/* Card content */}
        <div className="pr-14 sm:pr-28 overflow-hidden">
          <div className="flex items-center gap-2 mb-1 min-w-0">
            <h3 className="text-sm sm:text-base font-semibold text-gray-800 dark:text-gray-100 truncate">
              {place.name}
            </h3>
            <a
              href={getMapsUrl(place.name, place.place_id)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-300 dark:text-gray-600 hover:text-honey-500 transition-colors flex-shrink-0"
              title="View on Google Maps"
              aria-label={`View ${place.name} on Google Maps`}
              onClick={(e) => e.stopPropagation()}
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
              </svg>
            </a>
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-500 truncate">{place.address}</p>
          {place.distance_meters != null && (
            <p className="text-xs text-honey-600 dark:text-honey-400 mt-0.5">
              {place.distance_meters < 1000
                ? `${Math.round(place.distance_meters)}m away`
                : `${(place.distance_meters / 1000).toFixed(1)}km away`}
            </p>
          )}

          {/* Occupancy badge */}
          <div className="mt-2">
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text_color}`}
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={badge.icon} />
              </svg>
              {badge.text}
            </span>
          </div>
        </div>

        {place.business_status !== 'CLOSED_TEMPORARILY' && (
          <OccupancyIndicator popularity={place.current_popularity} />
        )}
      </div>
    </div>
  );
};

PlaceCard.propTypes = {
  place: PropTypes.shape({
    name: PropTypes.string.isRequired,
    address: PropTypes.string.isRequired,
    current_popularity: PropTypes.number,
    business_status: PropTypes.string,
    place_id: PropTypes.string,
    distance_meters: PropTypes.number,
  }).isRequired,
  buttonText: PropTypes.string.isRequired,
  onButtonClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default PlaceCard;
